import { memo, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { ProfilingRequestSource } from 'clickNgo/configs/profiling/common'
import {
  ContactStatus,
  ContactWorkflowStatus,
  IContact,
  LoadingState,
} from 'clickNgo/entities/contact'
import { IContactFlags } from 'clickNgo/entities/contactFlag'
import { ContactDeactivationModal } from 'clickNgo/features/contact/contactDeactivation'
import { ContactRelinkingValidationModal } from 'clickNgo/features/contact/contactRelinking'
import { formatErrorMessages } from 'clickNgo/helpers/api'
import { IAccessibility } from 'clickNgo/models/widget'
import { InfoBlock, InfoBlockRounded, InfoBlockType } from 'cng-common/components/infoBlock'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { useGetFormDataField } from 'cng-common/features/form'
import { Translation } from 'cng-common/features/translations'
import { Nullable } from 'cng-common/models/utils'
import { Button } from 'core-components/library/button'

import { ContactEditorFormKeys } from '../../configs'
import { useReadOnlyMenuItem, useValidateForm } from '../../hooks'
import {
  ContactEditorSettingFormFields,
  ContactFormMenuItems,
  TContactEditorData,
  THandlerResult,
} from '../../models'
import { contactEditorTranslation } from '../../translation'
import { ContactWorkflowStatusPanel } from '../contactWorkflowStatusPanel'

import { useEditMenuItems, useRemoveRequest } from './hooks'

import styles from './ContactEditableContainer.module.scss'

export interface IOnApplyProps {
  newHpRoles?: string[]
  workflowStatus?: ContactWorkflowStatus
}

type Props = {
  children: ReactNode
  contact: IContact
  contactFlags: IContactFlags
  onApply: ({ newHpRoles, workflowStatus }: IOnApplyProps) => Promise<THandlerResult>
  onSaveAsDraft?: () => Promise<THandlerResult>
  onCancel: () => Promise<THandlerResult>
  onUpdate: (contact: Nullable<IContact>) => Promise<void> | void
  hasModifiedValues: boolean
} & Pick<IAccessibility, 'isReadOnly'>

export const ContactEditableContainer = memo<Props>(
  ({
    children,
    contact,
    contactFlags,
    hasModifiedValues,
    onApply,
    onSaveAsDraft,
    onCancel,
    onUpdate,
  }) => {
    const { removeRequest } = useRemoveRequest(contact, onUpdate)
    const [actualData, updateActualData] = useGetFormDataField<TContactEditorData>(
      ContactEditorFormKeys.settings,
      ContactEditorSettingFormFields.ActualData
    )
    const { hasInvalidData } = useValidateForm()

    const { currentMenuItem, isCurrentMenuItemEditable, setIsCurrentMenuItemEditable } =
      useEditMenuItems()
    const { isReadOnly } = useReadOnlyMenuItem()

    const [isUpdateSuccessful, setIsUpdateSuccessful] = useState(false)
    const [isContactDeactivationVisible, setIsContactDeactivationVisible] = useState(false)
    const [isContactRelinkingValidationVisible, setIsContactRelinkingValidationVisible] =
      useState(false)

    const [isActionPending, setIsActionPending] = useState(false)
    const isActionPendingRef = useRef(false)

    const [relinkingNewPartnerId] = useGetFormDataField<string>(
      ContactEditorFormKeys.contactRelinking,
      'newPartnerId'
    )
    const [loadingState] = useGetFormDataField<LoadingState>(
      ContactEditorFormKeys.settings,
      ContactEditorSettingFormFields.IsLoading
    )
    const [errorMessages] = useGetFormDataField<string[]>(
      ContactEditorFormKeys.settings,
      ContactEditorSettingFormFields.ErrorMessages
    )

    const isNewContact = contact.contactId?.startsWith('new-')
    const isAwaitingCreation = contact.workflowStatus === ContactWorkflowStatus.AwaitingCreation
    const isInactiveContact = contact.status === ContactStatus.Inactive
    const isContactRelinking = Boolean(contact.newPartnerId && contact.workflowStatus)
    const isOnboardingRequestContact = contact.source?.startsWith(ProfilingRequestSource.Onboarding)
    const isEditButtonVisible =
      !isCurrentMenuItemEditable &&
      !isReadOnly &&
      !isInactiveContact &&
      !isAwaitingCreation &&
      (!isContactRelinking ||
        isOnboardingRequestContact ||
        currentMenuItem === ContactFormMenuItems.ContactRelinking)
    const isInactiveButtonVisible = !isNewContact && !isInactiveContact
    const isHeaderVisible = !isInactiveContact && !isAwaitingCreation && !isReadOnly
    const isSaveAsDraftButtonVisible = Boolean(
      onSaveAsDraft &&
        isCurrentMenuItemEditable &&
        contact.workflowStatus === ContactWorkflowStatus.Draft
    )
    const isActionInProgress = Boolean(loadingState?.isLoading || isActionPending)

    const formattedErrorMessages = useMemo(
      () => formatErrorMessages(errorMessages),
      [errorMessages]
    )

    // Shared guard: prevents concurrent submissions from any action button.
    // Returns early with an error result if an action is already in flight.
    const runAction = useCallback(
      async (action: () => Promise<THandlerResult | void>): Promise<THandlerResult> => {
        if (isActionPendingRef.current) {
          return { hasError: true }
        }

        isActionPendingRef.current = true
        setIsActionPending(true)
        setIsUpdateSuccessful(false)

        try {
          const result = (await action()) ?? {}

          if (!result.hasError) {
            setIsCurrentMenuItemEditable(false)
            setIsUpdateSuccessful(true)
          }

          return result
        } finally {
          isActionPendingRef.current = false
          setIsActionPending(false)
        }
      },
      [setIsCurrentMenuItemEditable]
    )

    const applyChanges = useCallback(
      (props: IOnApplyProps = {}) => runAction(() => onApply(props)),
      [onApply, runAction]
    )

    const discardChanges = useCallback(async () => {
      await onCancel()
      setIsCurrentMenuItemEditable(false)
    }, [onCancel, setIsCurrentMenuItemEditable])

    /** ---------------------- Handlers ------------------------ */

    const handleInactivateClick = useCallback(() => {
      // setIsInactiveConfirmDialogVisible(true)
      setIsContactDeactivationVisible(true)
    }, [])

    const handleContactDeactivate = useCallback(
      async (deactivatedContact: IContact) => {
        await onUpdate(deactivatedContact)
        updateActualData({
          ...actualData,
          contact: deactivatedContact,
        })
        setIsContactDeactivationVisible(false)
      },
      [actualData, onUpdate, updateActualData]
    )

    const handleContactRelink = useCallback(
      async (newHpRoles?: string[]) => {
        setIsContactRelinkingValidationVisible(false)
        await applyChanges({ newHpRoles })
      },
      [applyChanges]
    )

    const handleContactDeactivationCancel = useCallback(() => {
      setIsContactDeactivationVisible(false)
    }, [])

    const handleContactRelinkValidationCancel = useCallback(() => {
      setIsContactRelinkingValidationVisible(false)
    }, [])

    const handleEditClick = useCallback(() => {
      setIsCurrentMenuItemEditable(true)
    }, [setIsCurrentMenuItemEditable])

    const handleApplyClick = useCallback(async () => {
      if (relinkingNewPartnerId && contact.newPartnerId !== relinkingNewPartnerId) {
        setIsContactRelinkingValidationVisible(true)
        return
      }
      applyChanges()
    }, [applyChanges, contact.newPartnerId, relinkingNewPartnerId])

    const handleCancelClick = useCallback(() => {
      discardChanges()
    }, [discardChanges])

    const handleSaveAsDraftClick = useCallback(async () => {
      if (onSaveAsDraft) {
        await runAction(onSaveAsDraft)
      }
    }, [onSaveAsDraft, runAction])

    useEffect(() => {
      return () => {
        if (isUpdateSuccessful) {
          setIsUpdateSuccessful(false)
        }
      }
    }, [isUpdateSuccessful])

    return (
      <div className={styles.container}>
        <RenderWhen condition={isUpdateSuccessful}>
          <InfoBlock
            text={contactEditorTranslation.warnings.successfulUpdate}
            type={InfoBlockType.Success}
            hasCloseButton
          />
        </RenderWhen>
        <RenderWhen condition={errorMessages.length > 0}>
          <div className={styles.errors}>
            <InfoBlock
              text={formattedErrorMessages}
              type={InfoBlockType.Danger}
              rounded={InfoBlockRounded.Medium}
            />
          </div>
        </RenderWhen>
        <RenderWhen condition={isContactRelinking}>
          <div className={styles.errors}>
            <InfoBlock
              text={contactEditorTranslation.warnings.relinking}
              type={InfoBlockType.Help}
              rounded={InfoBlockRounded.Medium}
            />
          </div>
        </RenderWhen>
        <RenderWhen condition={isInactiveContact}>
          <div className={styles.errors}>
            <InfoBlock
              text={contactEditorTranslation.warnings.inactivateContact}
              type={InfoBlockType.Danger}
              rounded={InfoBlockRounded.Medium}
            />
          </div>
        </RenderWhen>
        <RenderWhen condition={isHeaderVisible}>
          <div className={styles.header}>
            <div style={{ width: '100%' }}>
              <ContactWorkflowStatusPanel contact={contact} onRemoveRequest={removeRequest} />
            </div>
            <div className={styles.actions}>
              <RenderWhen condition={isInactiveButtonVisible}>
                <Button
                  color="warning"
                  disabled={isActionInProgress}
                  onClick={handleInactivateClick}
                >
                  <Translation text={contactEditorTranslation.container.button.inactive} />
                </Button>
              </RenderWhen>
              <RenderWhen condition={isEditButtonVisible}>
                <Button disabled={isActionInProgress} onClick={handleEditClick}>
                  <Translation text={contactEditorTranslation.container.button.edit} />
                </Button>
              </RenderWhen>
              <RenderWhen condition={isSaveAsDraftButtonVisible}>
                <Button
                  className={styles.actionButton}
                  disabled={!hasModifiedValues || hasInvalidData || isActionInProgress}
                  onClick={handleSaveAsDraftClick}
                >
                  Save as Draft
                </Button>
              </RenderWhen>
              <RenderWhen condition={isCurrentMenuItemEditable}>
                <Button
                  disabled={!hasModifiedValues || hasInvalidData || isActionInProgress}
                  onClick={handleApplyClick}
                >
                  <Translation text={contactEditorTranslation.container.button.apply} />
                </Button>
              </RenderWhen>
              <RenderWhen condition={isCurrentMenuItemEditable}>
                <Button color="danger" disabled={isActionInProgress} onClick={handleCancelClick}>
                  <Translation text={contactEditorTranslation.container.button.cancel} />
                </Button>
              </RenderWhen>
            </div>
          </div>
        </RenderWhen>
        <div className={styles.content}>{children}</div>
        <ContactDeactivationModal
          contact={contact}
          contactFlags={contactFlags}
          isVisible={isContactDeactivationVisible}
          onDeactivate={handleContactDeactivate}
          onClose={handleContactDeactivationCancel}
        />
        <ContactRelinkingValidationModal
          contact={contact}
          contactFlags={contactFlags}
          isVisible={isContactRelinkingValidationVisible}
          onRelink={handleContactRelink}
          onClose={handleContactRelinkValidationCancel}
          relinkingNewPartnerId={relinkingNewPartnerId}
        />
      </div>
    )
  }
)

ContactEditableContainer.displayName = 'ContactEditableContainer'
